import { Box, CircularProgress, Typography } from "@mui/material";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { allowedPathsByTokenType, decodeAndValidateToken } from "@utils/jwtUtils";

import authService, { AUTH_KEY } from "../../../services/authService";
import { IAppState } from "../../../store/reducers";
import { AuthState } from "../../../store/reducers/auth";

interface ConditionalAuthGuardProps {
  children: React.ReactNode;
}

const TOKEN_STORAGE_KEY = "BYPASS_AUTH_KEY";

export const ConditionalAuthGuard: React.FC<ConditionalAuthGuardProps> = ({ children }) => {
  console.log('on ConditionalAuthGuard')
  const location = useLocation();
  const history = useHistory();
  const { authUser } = useSelector<IAppState, AuthState>((state) => state.auth);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token") || authService.getBypassToken();

    if (token) {
      // try {
      const decodedToken = decodeAndValidateToken(token);

      if (!decodedToken) {
        setError("Invalid or expired access link.");
        setIsLoading(false);
        return;
      }

      const allowedPaths = allowedPathsByTokenType[decodedToken.type];
      if (!allowedPaths) {
        setError("Invalid or expired access link.");
        setIsLoading(false);
        return;
      }

      // Validate path
      if (!allowedPaths.some((path: string) => location.pathname.startsWith(path))) {
        setError("Access denied to this page.");
        setIsLoading(false);
        return;
      }

      localStorage.removeItem(AUTH_KEY);

      // Store token data in localStorage
      localStorage.setItem(
        TOKEN_STORAGE_KEY,
        JSON.stringify({
          token: token,
          ..._.omit(decodedToken, "iat", "exp", "permissions"),
        }),
      );

      setIsLoading(false);
      // } catch (err) {
      //   setError('Invalid or expired access link.');
      //   setIsLoading(false);
      // }
    } else {
      // Standard auth flow
      if (!authUser?.id) {
        setError("You have been logged out.");
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    }
  }, [location, authUser, history]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error === "You have been logged out." && !["/signInPage", "/"].includes(history.location.pathname)) {
    setTimeout(() => {
      history.push("/signInPage");
    }, 3000);

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          flexDirection: "column",
          textAlign: "center",
          padding: 2,
        }}
      >
        <Typography variant="h6" gutterBottom>
          You have been logged out
        </Typography>
        <Typography>Redirecting to sign-in page...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          flexDirection: "column",
          textAlign: "center",
          padding: 2,
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Access Error
        </Typography>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  return <>{children}</>;
};
